import { z } from 'zod';

/**
 * Фирменный стиль (§13) и Brand Context (§27).
 *
 * Отвечает за оформление ГОТОВОГО КОНТЕНТА, а не за внешность пользователя.
 * Ключевое правило §27: AI не придумывает визуальный язык каждый раз —
 * любой генератор графики получает brand context на вход.
 */

const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Ожидается цвет в формате #RRGGBB');

/** §13: логотип — файл, версия, положение, safe area. */
export const logoSchema = z
  .object({
    assetId: z.string().nullable().default(null),
    position: z.enum(['top-left', 'top-right', 'bottom-left', 'bottom-right']).default('top-right'),
    /** Отступ от края в процентах короткой стороны кадра. */
    safeAreaPercent: z.number().min(0).max(20).default(5),
    opacity: z.number().min(0).max(1).default(0.9),
    sizePercent: z.number().min(2).max(30).default(9),
  })
  .strict();

/** §13: основной, дополнительный, accent, текст, фон. */
export const colorsSchema = z
  .object({
    background: hexColor.default('#0E1013'),
    primary: hexColor.default('#5B8CFF'),
    secondary: hexColor.default('#9B9FA6'),
    accent: hexColor.default('#4BAE83'),
    text: hexColor.default('#F3F4F5'),
  })
  .strict();

const fontSlot = z
  .object({
    family: z.string().min(1).default('Inter'),
    /** Свой шрифт — обычный Asset с kind=font (agents/api-looks-brand.md §4). */
    assetId: z.string().nullable().default(null),
    weight: z.number().int().min(100).max(900).default(600),
  })
  .strict();

/** §13: заголовки, основной текст, титры, цифры. */
export const typographySchema = z
  .object({
    heading: fontSlot.prefault({ weight: 700 }),
    body: fontSlot.prefault({ weight: 400 }),
    captions: fontSlot.prefault({ weight: 600 }),
    numbers: fontSlot.prefault({ weight: 700 }),
  })
  .strict();

/** §13, §28: расположение, строки, размер, цвет, фон, активное слово, анимация. */
export const captionsSchema = z
  .object({
    enabled: z.boolean().default(true),
    position: z.enum(['bottom', 'center', 'top']).default('bottom'),
    maxLines: z.number().int().min(1).max(3).default(2),
    sizePercent: z.number().min(3).max(12).default(6),
    color: hexColor.default('#F3F4F5'),
    background: z.enum(['none', 'solid', 'shadow']).default('shadow'),
    backgroundColor: hexColor.default('#090A0B'),
    activeWordColor: hexColor.default('#5B8CFF'),
    animation: z.enum(['none', 'fade', 'pop', 'karaoke']).default('karaoke'),
  })
  .strict();

/** §13: имя, должность, стиль. */
export const lowerThirdSchema = z
  .object({
    name: z.string().max(80).default(''),
    title: z.string().max(120).default(''),
    style: z.enum(['minimal', 'bar', 'accent']).default('minimal'),
    position: z.enum(['bottom-left', 'bottom-center']).default('bottom-left'),
  })
  .strict();

/** §13: заголовок, подзаголовок, цитата, тезис, цифра, список. */
export const textBlocksSchema = z
  .object({
    alignment: z.enum(['left', 'center']).default('left'),
    heading: z
      .object({ sizePercent: z.number().min(4).max(20).default(9) })
      .strict()
      .prefault({}),
    subheading: z
      .object({ sizePercent: z.number().min(3).max(14).default(5) })
      .strict()
      .prefault({}),
    quote: z
      .object({ style: z.enum(['plain', 'marked']).default('marked') })
      .strict()
      .prefault({}),
    statement: z
      .object({ style: z.enum(['plain', 'boxed']).default('plain') })
      .strict()
      .prefault({}),
    number: z
      .object({ sizePercent: z.number().min(8).max(40).default(22) })
      .strict()
      .prefault({}),
    list: z
      .object({ marker: z.enum(['dot', 'dash', 'number']).default('dot') })
      .strict()
      .prefault({}),
  })
  .strict();

/** §13: текст, кнопка, оформление, финальный экран. */
export const ctaSchema = z
  .object({
    text: z.string().max(60).default('Попробовать'),
    buttonStyle: z.enum(['solid', 'outline', 'text']).default('solid'),
    buttonColor: hexColor.default('#5B8CFF'),
    buttonTextColor: hexColor.default('#090A0B'),
    finalScreen: z.enum(['logo', 'cta', 'both', 'none']).default('both'),
  })
  .strict();

/** §13: цвет, изображение, видео, брендовые фоны. */
/**
 * §31, B3: фоновая музыка ролика.
 *
 * Настройка живёт в бренд-ките, а не в проекте: музыка — часть звучания
 * бренда, и подбирать её заново для каждого ролика человек не должен.
 */
/**
 * Подача речи (§29-30).
 *
 * Скорость — единственная ручка, которую провайдер синтеза принимает
 * на самом деле: высота и «эмоция» отдельными полями отвергаются, проверено
 * запросами. Всё остальное задаётся разметкой самой реплики.
 *
 * Живёт в бренд-ките, а не в проекте: темп речи — часть того, как человек
 * звучит вообще, а не решение под конкретный ролик.
 */
export const voiceStyleSchema = z
  .object({
    /** 0.5-2.0 — границы провайдера. Единица означает «как записан голос». */
    speed: z.number().min(0.5).max(2).default(1),
  })
  .strict();

export const musicSchema = z
  .object({
    enabled: z.boolean().default(false),
    /** Настроение словами: подбор — задача музыкального провайдера. */
    mood: z.string().trim().max(120).default('спокойная фоновая музыка без вокала'),
    /**
     * Громкость относительно речи, 0-1.
     *
     * По умолчанию тихо: музыка, заглушающая голос, — главная причина,
     * по которой её выключают совсем.
     */
    volume: z.number().min(0).max(1).default(0.18),
  })
  .strict();

export const backgroundsSchema = z
  .object({
    color: hexColor.default('#0E1013'),
    imageAssetId: z.string().nullable().default(null),
    videoAssetId: z.string().nullable().default(null),
    brandAssetIds: z.array(z.string()).default([]),
  })
  .strict();

/** §13, §70: скорость, появление, исчезновение, стиль переходов. */
export const motionSchema = z
  .object({
    speed: z.enum(['calm', 'normal', 'dynamic']).default('normal'),
    appear: z.enum(['fade', 'slide', 'scale']).default('fade'),
    disappear: z.enum(['fade', 'slide', 'scale']).default('fade'),
    transition: z.enum(['cut', 'fade', 'slide']).default('fade'),
  })
  .strict();

export const brandKitSchema = z
  .object({
    name: z.string().trim().min(1).max(60).default('Основной'),
    logo: logoSchema.prefault({}),
    colors: colorsSchema.prefault({}),
    typography: typographySchema.prefault({}),
    captions: captionsSchema.prefault({}),
    lowerThird: lowerThirdSchema.prefault({}),
    textBlocks: textBlocksSchema.prefault({}),
    cta: ctaSchema.prefault({}),
    backgrounds: backgroundsSchema.prefault({}),
    music: musicSchema.prefault({}),
    voice: voiceStyleSchema.prefault({}),
    motion: motionSchema.prefault({}),
  })
  .strict();

export type BrandKitInput = z.infer<typeof brandKitSchema>;

/**
 * Частичное обновление: экран стиля сохраняет по одному блоку.
 *
 * ВАЖНО: строим явно через `.optional()`, а НЕ через `brandKitSchema.partial()`.
 * `.partial()` не снимает `.prefault()`, поэтому отсутствующие блоки приходили бы
 * заполненными умолчаниями — и патч одних только цветов молча сбрасывал бы
 * настройки титров, CTA и всего остального.
 *
 * Семантика поблочная: пришёл блок — заменяется целиком, не пришёл — не трогаем.
 */
export const updateBrandKitSchema = z
  .object({
    name: z.string().trim().min(1).max(60).optional(),
    logo: logoSchema.optional(),
    colors: colorsSchema.optional(),
    typography: typographySchema.optional(),
    captions: captionsSchema.optional(),
    lowerThird: lowerThirdSchema.optional(),
    textBlocks: textBlocksSchema.optional(),
    cta: ctaSchema.optional(),
    backgrounds: backgroundsSchema.optional(),
    motion: motionSchema.optional(),
    /**
     * Музыка (B3).
     *
     * Забыть её здесь — значит сделать настройку, которую видно в интерфейсе
     * и невозможно сохранить: схема строгая, и запрос отвергается целиком
     * с «неизвестное поле».
     */
    music: musicSchema.optional(),
    voice: voiceStyleSchema.optional(),
  })
  .strict();

export interface BrandKitView extends BrandKitInput {
  id: string;
  isActive: boolean;
  schemaVersion: number;
  /** Подписанные ссылки на логотип и фоны — для превью. */
  logoUrl: string | null;
  backgroundImageUrl: string | null;
  updatedAt: string;
}

/**
 * Brand Context (§27) — то, что получают ВСЕ генераторы графики.
 *
 * Плоское производное представление кита. Промпт без brand context —
 * ошибка ревью (agents/api-looks-brand.md §3).
 */
export interface BrandContext {
  colors: {
    background: string;
    primary: string;
    secondary: string;
    accent: string;
    text: string;
  };
  fonts: {
    heading: string;
    body: string;
    captions: string;
    numbers: string;
  };
  logo: {
    url: string | null;
    position: string;
    safeAreaPercent: number;
    sizePercent: number;
    opacity: number;
  };
  /** Короткая форма §27: «положение/анимация/фон». */
  captionStyle: string;
  /**
   * Настройки титров целиком (§28).
   *
   * Строка `captionStyle` теряет цвета и размер, а рисовать титры без них
   * нельзя: активное слово в чужом цвете — это уже не бренд-кит.
   */
  captions: {
    enabled: boolean;
    position: string;
    maxLines: number;
    sizePercent: number;
    color: string;
    background: string;
    backgroundColor: string;
    activeWordColor: string;
    animation: string;
  };
  buttonStyle: string;
  motionStyle: string;
  /** Формат кадра, под который собран контекст. */
  aspectRatio: string;
}
